import { useState, useEffect } from 'react'
import { api } from '../lib/api'
import { useDatePreferences } from '../hooks/useDatePreferences'
import {
  Activity,
  Search,
  Filter,
  ChevronDown,
  Download,
  User,
  FileText,
  Shield,
  AlertTriangle,
} from 'lucide-react'

interface AuditLog {
  id: string
  user_id: string
  username: string
  user_display_name?: string
  action: string
  resource_type: string
  resource_id: string
  details?: string
  ip_address?: string
  status: string
  timestamp: number
}

const resourceIcons: Record<string, any> = {
  control: Shield,
  evidence: FileText,
  risk: AlertTriangle,
  user: User,
}

const actionColors: Record<string, string> = {
  created: 'text-green-600',
  updated: 'text-blue-600',
  deleted: 'text-red-600',
  uploaded: 'text-purple-600',
  exported: 'text-gray-600',
  generated: 'text-gray-600',
}

export default function ActivityLog() {
  const { formatDateTime } = useDatePreferences()
  const [logs, setLogs] = useState<AuditLog[]>([])
  const [loading, setLoading] = useState(true)
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(0)
  const [search, setSearch] = useState('')
  const [resourceFilter, setResourceFilter] = useState('')
  const [showFilters, setShowFilters] = useState(false)

  const limit = 50

  useEffect(() => {
    async function fetchLogs() {
      setLoading(true)
      try {
        const res = await api.getAuditLogs({
          limit,
          offset: page * limit,
          search: search || undefined,
          resource_type: resourceFilter || undefined,
        })
        if (res.success) {
          setLogs(res.logs)
          setTotal(res.pagination.total)
        }
      } catch (err) {
        console.error('Failed to load audit logs:', err)
      } finally {
        setLoading(false)
      }
    }
    fetchLogs()
  }, [page, search, resourceFilter])

  const handleExport = async () => {
    try {
      const token = localStorage.getItem('grc_token')
      const response = await fetch(
        `${import.meta.env.VITE_API_URL || 'https://grc-tenant-dev.port-technology-account.workers.dev'}/api/audit/export`,
        {
          headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        }
      )
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `audit-log-${new Date().toISOString().split('T')[0]}.csv`
      a.click()
    } catch (err) {
      console.error('Failed to export audit logs:', err)
    }
  }

  const formatDate = (ts: number) => {
    return formatDateTime(ts)
  }

  const getActionColor = (action: string) => {
    for (const [key, color] of Object.entries(actionColors)) {
      if (action.includes(key)) return color
    }
    return 'text-gray-600'
  }

  const formatAction = (action: string) => {
    return action.replace(/\./g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2')
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Activity Log</h1>
          <p className="text-gray-500">Track all actions and changes in the system</p>
        </div>
        <button onClick={handleExport} className="btn-secondary">
          <Download className="h-4 w-4 mr-2" />
          Export CSV
        </button>
      </div>

      {/* Search and Filters */}
      <div className="card p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search activity..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value)
                setPage(0)
              }}
              className="input pl-10"
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="btn-secondary"
          >
            <Filter className="h-4 w-4 mr-2" />
            Filters
            <ChevronDown
              className={`h-4 w-4 ml-2 transition-transform ${
                showFilters ? 'rotate-180' : ''
              }`}
            />
          </button>
        </div>

        {showFilters && (
          <div className="mt-4 pt-4 border-t">
            <div className="max-w-xs">
              <label className="label">Resource Type</label>
              <select
                value={resourceFilter}
                onChange={(e) => {
                  setResourceFilter(e.target.value)
                  setPage(0)
                }}
                className="input"
              >
                <option value="">All Types</option>
                <option value="control">Controls</option>
                <option value="evidence">Evidence</option>
                <option value="risk">Risks</option>
                <option value="user">Users</option>
                <option value="report">Reports</option>
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Activity List */}
      <div className="card">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
          </div>
        ) : logs.length === 0 ? (
          <div className="text-center py-12">
            <Activity className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">No activity found</p>
          </div>
        ) : (
          <>
            <div className="divide-y divide-gray-200">
              {logs.map((log) => {
                const ResourceIcon = resourceIcons[log.resource_type] || Activity
                return (
                  <div key={log.id} className="p-4 hover:bg-gray-50">
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0 mt-1">
                        <div className="h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center">
                          <ResourceIcon className="h-4 w-4 text-gray-600" />
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900">
                            {log.user_display_name || log.username || 'System'}
                          </span>
                          <span className={`text-sm ${getActionColor(log.action)}`}>
                            {formatAction(log.action)}
                          </span>
                        </div>
                        {log.details && (
                          <p className="text-sm text-gray-600 mt-0.5">{log.details}</p>
                        )}
                        <div className="flex items-center gap-4 mt-1 text-xs text-gray-500">
                          <span>{formatDate(log.timestamp)}</span>
                          <span className="capitalize">{log.resource_type}</span>
                          {log.ip_address && <span>IP: {log.ip_address}</span>}
                        </div>
                      </div>
                      <div className="flex-shrink-0">
                        <span
                          className={`badge-${log.status === 'success' ? 'green' : 'red'}`}
                        >
                          {log.status}
                        </span>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>

            {/* Pagination */}
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
              <p className="text-sm text-gray-500">
                Showing {page * limit + 1} to {Math.min((page + 1) * limit, total)} of{' '}
                {total} entries
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0}
                  className="btn-secondary disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPage((p) => p + 1)}
                  disabled={(page + 1) * limit >= total}
                  className="btn-secondary disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
